// re.rejouis.fr : OFFRES DU MOMENT (re.rejouis.fr/offres, lien de la FAQ rejouis.fr).
// Deux offres posées au lab (onglet Catalogue), cumulables sur un même modèle :
//  - modèles recherchés (Prio) : crédit boosté pour une durée limitée (prio.js) ;
//  - rachat en euros : virement possible à la place du bon d'achat (rachat.js).
// Liste filtrable (Tout / Virement / Boostés + nom) pour rester légère. Pas de montant
// sur les cartes : il s'affiche à l'étape d'état, une fois l'état décrit.
// Budget virement du mois épuisé (ou indisponible) : les modèles « virement seul »
// sortent de la liste, pour ne rien promettre que l'étape d'état ne proposerait pas.
import { useEffect, useState } from 'react';
import { EcranHeader, Icons, Thumb, Sticker, Spinner, PastilleBoost } from '../components/ui.jsx';
import PanierBar from '../components/PanierBar.jsx';
import { offresDuMoment, disponibiliteVirement } from '../lib/api.js';
import { boostPrio, finPrio } from '../lib/prio.js';
import { pctVirement } from '../lib/rachat.js';
import { suivre } from '../lib/posthog.js';

const dateCourte = (iso) => new Date(iso + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });

// Ligne d'un modèle en offre : vignette, nom, « +30 % jusqu'au 22 novembre », « Virement possible ».
// `virement` : false quand le budget du mois est épuisé (la mention disparaît).
export function CarteRecherche({ p, dansPanier, onChoisir, virement = true }) {
  const boost = boostPrio(p);
  const fin = finPrio(p);
  const vir = virement && pctVirement(p) > 0;
  return (
    <button onClick={() => onChoisir(p)} className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, width: '100%', textAlign: 'left' }}>
      <Thumb produit={p} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 13.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.titre}</div>
        <div style={{ fontSize: 11.5, color: 'var(--stone)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {[p.marque, p.categorie].filter(Boolean).join(' · ')}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginTop: 5 }}>
          {boost > 0 ? (
            <>
              <PastilleBoost boost={boost} fin={fin} style={{ position: 'relative' }} />
              <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--rouge)' }}>
                +{Math.round(boost * 100)} %{fin ? ` jusqu’au ${dateCourte(fin)}` : ''}
              </span>
            </>
          ) : null}
          {vir ? <span className="badge badge-mint">Virement possible</span> : null}
        </div>
      </div>
      <span aria-label={dansPanier ? 'Déjà au panier' : 'Estimer'}
        style={{ width: 38, height: 38, borderRadius: 999, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: dansPanier ? 'var(--mint)' : 'var(--rouge)', color: dansPanier ? 'var(--noir)' : '#fff' }}>
        {dansPanier ? <Icons.check size={18} sw={2.2} /> : <Icons.chev size={18} sw={2.2} />}
      </span>
    </button>
  );
}

const sansAccent = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export default function Recherches({ go, panier, onChoisir }) {
  const [produits, setProduits] = useState(null);
  const [virementOk, setVirementOk] = useState(false);
  const [erreur, setErreur] = useState(false);
  const [filtre, setFiltre] = useState('tout'); // tout | virement | boost
  const [q, setQ] = useState('');
  const idsPanier = new Set(panier.map((l) => l.produitId));

  useEffect(() => {
    let vivant = true;
    Promise.all([
      offresDuMoment(),
      disponibiliteVirement().catch(() => ({ disponible: false })),
    ])
      .then(([r, d]) => {
        if (!vivant) return;
        const ok = !!d?.disponible;
        setVirementOk(ok);
        setProduits((r.produits || []).filter((p) => boostPrio(p) > 0 || (ok && pctVirement(p) > 0)));
      })
      .catch(() => { if (vivant) { setErreur(true); setProduits([]); } });
    return () => { vivant = false; };
  }, []);

  const liste = produits || [];
  const nbVir = virementOk ? liste.filter((p) => pctVirement(p) > 0).length : 0;
  const nbBoost = liste.filter((p) => boostPrio(p) > 0).length;
  const terme = sansAccent(q.trim());
  const visibles = liste
    .filter((p) => filtre === 'tout' || (filtre === 'virement' ? pctVirement(p) > 0 : boostPrio(p) > 0))
    .filter((p) => !terme || sansAccent(`${p.titre} ${p.marque} ${p.categorie || ''}`).includes(terme));

  const choisir = (p) => {
    suivre('wanted_model_selected', {
      boost_pct: Math.round(boostPrio(p) * 100),
      virement: virementOk && pctVirement(p) > 0,
      source: 'ecran_offres',
      filtre,
    });
    onChoisir(p);
  };

  const Puce = ({ id, label, n }) => (
    <button className={'chip' + (filtre === id ? ' actif' : '')} onClick={() => setFiltre(id)}>
      {label} <span style={{ opacity: 0.7 }}>{n}</span>
    </button>
  );

  return (
    <div className="no-scrollbar" style={{ height: '100%', overflow: 'auto', padding: '16px 0 110px' }}>
      <EcranHeader titre="Offres du moment" onBack={() => go('accueil')} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px 18px 14px' }}>
        <Sticker nom="coeur-etincelle" size={48} rot={-8} />
        <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: 'var(--encre-doux)' }}>
          Les modèles qu’on recherche : <strong>crédit boosté</strong>{virementOk ? <> ou <strong>virement possible</strong></> : null}.
        </p>
      </div>

      {produits === null ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}><Spinner label="Chargement…" /></div>
      ) : liste.length ? (
        <>
          {nbVir && nbBoost ? (
            <div className="no-scrollbar" style={{ display: 'flex', gap: 8, padding: '0 16px 10px', overflowX: 'auto' }}>
              <Puce id="tout" label="Tout" n={liste.length} />
              <Puce id="virement" label="Virement" n={nbVir} />
              <Puce id="boost" label="Boostés" n={nbBoost} />
            </div>
          ) : null}
          {liste.length > 8 ? (
            <div style={{ padding: '0 16px 12px' }}>
              <div style={{ position: 'relative' }}>
                <Icons.search size={17} style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: 'var(--stone)' }} />
                <input className="search-input" placeholder="Filtrer par nom ou marque" value={q} onChange={(e) => setQ(e.target.value)} />
              </div>
            </div>
          ) : null}
          <div style={{ padding: '0 16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 10 }}>
            {visibles.map((p) => (
              <CarteRecherche key={p.id} p={p} virement={virementOk} dansPanier={idsPanier.has(p.id)} onChoisir={choisir} />
            ))}
          </div>
          {!visibles.length ? (
            <div style={{ textAlign: 'center', padding: '20px 10px', fontSize: 13.5, color: 'var(--stone)' }}>Aucune offre pour « {q.trim()} ».</div>
          ) : null}
          <div style={{ padding: '16px 18px 0', fontSize: 12, color: 'var(--stone)', lineHeight: 1.5 }}>
            Le montant exact s’affiche quand tu décris l’état.{virementOk ? ' Virement : 70 % du bon d’achat, dans la limite du budget du mois.' : ''}
            {' '}Ton modèle n’y est pas ?{' '}
            <button onClick={() => go('catalogue', { focus: true })} style={{ color: 'var(--rouge)', fontWeight: 600, padding: 0 }}>Cherche-le</button>, on reprend 2 000+ références.
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '30px 10px', fontSize: 13.5, color: 'var(--stone)', lineHeight: 1.6 }}>
          {erreur ? 'La liste n’a pas pu être chargée.' : 'Aucune offre en ce moment.'}<br />
          <button onClick={() => go('catalogue', { focus: true })} style={{ marginTop: 10, color: 'var(--rouge)', fontWeight: 600, textDecoration: 'underline' }}>
            Chercher ton modèle
          </button>
        </div>
      )}
      <PanierBar panier={panier} go={go} />
    </div>
  );
}
